import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from "react-i18next";

import {
  Card,
  Row,
  Col,
  Space,
  Progress,
  Button,
  Select,
  List,
  Avatar,
  Tooltip
} from '@lockerpm/design';

import {
  SendOutlined,
  DeleteOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import common from "../../../utils/common";
import global from "../../../config/global";
import familyServices from "../../../services/family";

const SettingsMembers = () => {
  const { PageHeader } = commonComponents;
  const { DocLink } = itemsComponents;
  const { t } = useTranslation();
  const plan = useSelector((state) => state.payment.plan)
  const userInfo = useSelector((state) => state.auth.userInfo);

  const [loading, setLoading] = useState(false);
  const [newMembers, setNewMembers] = useState([]);
  const [callingAPI, setCallingAPI] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);

  const isFamilyOwner = common.isFamilyOwner();

  useEffect(() => {
    getFamilyMembers();
  }, [plan])

  const isLifeTimeUser = useMemo(() => {
    return common.isLifeTimeUser();
  }, [plan, userInfo])

  const isFamilyUser = useMemo(() => {
    return common.isFamilyUser();
  }, [plan])

  const maxNumber = useMemo(() => {
    return plan?.max_number || 6;
  }, [plan])

  const getFamilyMembers = async () => {
    setLoading(true);
    await familyServices.get_members().then((response) => {
      setFamilyMembers(response);
    }).catch(() => {
      setFamilyMembers([])
    })
    setLoading(false);
  }

  const handleChange = (emails) => {
    const validEmails = emails.filter((email) => {
      const regex = global.patterns.EMAIL;
      const addedEmails = familyMembers.map((m) => m.email);
      return regex.test(email) && !addedEmails.includes(email);
    })
    if (validEmails.length <= maxNumber - familyMembers.length) {
      setNewMembers(validEmails)
    }
  }

  const inviteMembers = async () => {
    setCallingAPI(true);
    familyServices.invite_members({ family_members: newMembers }).then(() => {
      global.pushSuccess(t('notification.success.manage_members.added'));
      getFamilyMembers();
    }).catch((error) => {
      global.pushError(error)
    })
    setNewMembers([])
    setCallingAPI(false);
  }


  const handleRemoveMember = (member) => {
    global.confirm(async () => {
      await familyServices.delete_member(member.id).then(() => {
        getFamilyMembers();
        global.pushSuccess(t('notification.success.manage_members.deleted'))
      }).catch((error) => {
        global.pushError(error)
      })
    }, {
      content: t('manage_members.question', { email: member.email }),
      okButtonProps: { danger: false },
    })
  }

  return (
    <div className="settings-members layout-content">
      <PageHeader
        title={t('manage_members.title')}
        actions={[]}
      />
      <Row gutter={[24, 24]}>
        <Col lg={16}>
          <Card className="h-full">
            <Space size={24}>
              <p className="text-xl">
                {t(common.displayPlanName())}
              </p>
              {
                !isLifeTimeUser && !isFamilyUser && <div className="">
                  <div>
                    <p>
                      <span className="text-4xl">${common.formatCurrency(plan?.yearly_price?.usd / 12, 2)}</span>
                      <Trans i18nKey={'manage_members.mo_per_users'} values={{ max_number: maxNumber }}/>
                    </p>
                    <p>
                      {t('manage_members.plan_desc', { price: common.formatCurrency(plan?.price?.usd, 2) })}
                      <Trans i18nKey={'manage_members.mo_per_users'} values={{ max_number: maxNumber }}/>
                    </p>
                  </div>
                </div>
              }
            </Space>
            <p className="mt-2 font-semibold">
              {t('manage_members.number_member_detail', { number: familyMembers.length, max_number: maxNumber })}
            </p>
            <Progress
              status="success"
              showInfo={false}
              percent={familyMembers.length/maxNumber * 100}
            />
          </Card>
        </Col>
        {
          !isLifeTimeUser && !isFamilyUser && <Col lg={8}>
            <Card className="h-full">
              <p>{t('common.next_billing_date')}</p>
              <p className="font-semibold text-xl">
                {common.convertDateTime(plan?.next_billing_time, 'LLL')}
              </p>
              <Button
                className="mt-6"
                type="primary"
                ghost
                onClick={() => common.clickSettingPlansBilling()}
              >
                {t('manage_members.button')}
              </Button>
            </Card>
          </Col>
        }
      </Row>
      <Card
        className="mt-4"
        title={t('manage_members.invite_title')}
      >
        {
          isFamilyOwner && <>
            <p className="font-semibold mb-2">{t('manage_members.invite_emails')}</p>
            <div className="flex">
              <Select
                mode="tags"
                className="w-full"
                size="large"
                value={newMembers}
                disabled={callingAPI}
                placeholder={t('manage_members.invite_placeholder')}
                dropdownStyle={{ display: 'none' }}
                onChange={(v) => handleChange(v)}
              />
              <Button
                size="large"
                className="ml-2"
                type="primary"
                ghost
                loading={callingAPI}
                disabled={newMembers.length === 0}
                icon={<SendOutlined />}
                onClick={() => inviteMembers()}
              >
                {t('button.invite')}
              </Button>
            </div>
            <p className="mt-2 mb-4">
              {t('manage_members.invite_emails_desc')}
            </p>
          </>
        }
        <div>
          <p className="font-semibold">{t('manage_members.added_member')} ({familyMembers.length}/{maxNumber})</p>
          <List
            itemLayout="horizontal"
            dataSource={familyMembers}
            loading={loading}
            renderItem={(item) => (
              <List.Item
                actions={item.id && isFamilyOwner ? [
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMember(item)}
                  >
                    {t('button.remove')}
                  </Button>
                ] : []}
              >
                <div className='flex items-center' style={{ width: '240px' }}>
                  <Avatar src={item.avatar} />
                  <div className='ml-2'>
                    {
                      item.full_name ? <p className="font-semibold text-primary">
                        {item.full_name} {item.email === userInfo.email ? `(${t('common.you')})` : ''}
                      </p> : <p className="font-semibold">
                        {t('common.unknown')} <Tooltip
                          placement="right"
                          title={<div>
                            <p className="text-sm">{t('manage_members.unknown_tooltip')}</p>
                            <DocLink
                              className="font-semibold text-sm mt-1"
                              title={t('common.learn_more')}
                              docKey="UPGRADING_TO_FAMILY_PLAN"
                            />
                          </div>}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </p> 
                    }
                    <p className='text-xs text-limited'>{item.email}</p>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </Card>
    </div>
  );
}

export default SettingsMembers;